import type { MapType } from '@/features/map/map-components/map-layer-switch/map-layer-switch';
import type { MapSwiperTile } from '@/features/map/map-components/map-swiper/map-swiper-state';
import type { CustomMapZoomControlEventType } from '@/features/map/map-components/map-zoom-control/map-zoom-control';
import type { MapDataV5_jsonld_page_read } from '@/features/map/map-data-v5/map-data-v5-type';
import type { IMapCluster } from '@/features/map/map-data-v5/map-types';

import React from 'react';

import useDynamicPageComponentByType from '@/core/features/a-dynamic-page/hooks/use-dynamic-page-component-by-type';
import { useDynamicPageUrlChange } from '@/core/features/a-dynamic-page/hooks/use-dynamic-page-path-change';
import { useClientUrl, useLayoutId } from '@/core/features/app/app-atoms';
import useAppEvents from '@/core/features/app/use-app-events';
import { useDeviceoutput } from '@/core/features/cookies/use-device-output';
import {
    triggerAppBottomSheet,
    triggerBoundariesChange,
} from '@/core/features/native-app-bridge/native-app-bridge-service';
import { scrollPageContainerToTop } from '@/core/features/page/page-element-serivce';
import { useAtom, useAtomValue, useSetAtom } from '@/core/features/store/atom-store';
import { useFilterDataV1Mutation } from '@/features/filter/filter-data/use-filter-data-mutation';
import { useGoogleMapInstance } from '@/features/map/google-map/google-map-state';
import {
    getStoredMapType,
    setStoredMapType,
} from '@/features/map/map-components/map-layer-switch/map-layer-switch-state';
import useMapPlaceRoutePreview from '@/features/map/map-components/map-markers/map-marker-handler/map-marker-switch/use-map-place-route-preview';
import { useMapSwiperState } from '@/features/map/map-components/map-swiper/map-swiper-state';
import { logMap } from '@/features/map/map-container/map-logger';
import {
    isGoogleMapInitializedAtom,
    useIsMapProjectionInitialized,
    useIsTempPositionReady,
    useMapActivePoiKeyState,
    useMapSize,
    useMapState,
    useMapVisitedPois,
    useMultiPolygons,
    useSetMapState,
    useSetSelectedMapTileHiddenAtom,
    useSetSelectedPoiKeyState,
} from '@/features/map/map-container/map-state';
import { useIsMapBottomSheetInitiallyOpen } from '@/features/map/map-container/use-is-map-bottom-sheet-initially-open';
import { useMapMovements } from '@/features/map/map-container/use-map-movements';
import useMapOverlayHistoryState from '@/features/map/map-container/use-map-overlay-history';
import { useMapPosition } from '@/features/map/map-container/use-map-position';
import {
    useAutoLoadMoreSetting,
    useHydrateAutoLoadMoreSetting,
} from '@/features/map/map-data-v5/auto-load-more-button-state';
import { convertPositionToBoundary, getBoundariesObject } from '@/features/map/map-data-v5/map-data-v5-service';
import {
    boundariesLoadMoreLivePricesTargetAtom,
    isMapDataV5ByBoundariesLoadingAtom,
    mapDataV5ByBoundariesAtom,
    mapDataV5DefaultAtom,
    setMapBoundariesAtom,
    useHydrateMapDataV5ByBoundaries,
    useHydrateMapDataV5ByPoi,
    useHydrateMapDataV5BySpot,
    useHydrateMapDataV5Default,
    useMultiPins,
    useUpdateMapClientUrl,
} from '@/features/map/map-data-v5/use-map-data-v5';

/**
 * Shared hook for map component
 * WARN: only use in map-component.tsx and map-component-desktop.tsx
 */
export default function useMapComponent() {
    // hydrate map states
    useHydrateMapDataV5ByPoi();
    useHydrateMapDataV5BySpot();
    useHydrateMapDataV5ByBoundaries();
    useHydrateMapDataV5Default();
    useHydrateAutoLoadMoreSetting();

    useMapPosition();
    const { jumpTo } = useMapMovements();

    const mapState = useMapState();
    const setMapState = useSetMapState();
    const layoutId = useLayoutId();
    const isMapWebView = layoutId === 'Mobile_Map_WebView_V1';
    const appEvents = useAppEvents();
    const isMapBottomSheetInitiallyOpen = useIsMapBottomSheetInitiallyOpen();
    const isTempPositionInitialized = useIsTempPositionReady();
    const [isMapProjectionInitialized, setIsMapProjectionInitialized] = useIsMapProjectionInitialized();

    const [clientUrl] = useClientUrl();
    const { saveMapStateToHistoryState } = useMapOverlayHistoryState();
    const { isDesktopView } = useDeviceoutput();

    const activePoiKey = useMapActivePoiKeyState();
    const [visitedPois, setVisitedPois] = useMapVisitedPois();
    const setActivePoiKey = useSetSelectedPoiKeyState();
    const setSelectedMapTileHiddenAtom = useSetSelectedMapTileHiddenAtom();
    const setMapBoundaries = useSetAtom(setMapBoundariesAtom);

    const setBoundariesLoadMoreLivePricesTarget = useSetAtom(boundariesLoadMoreLivePricesTargetAtom);

    const [isGoogleMapInitialized, setGoogleMapInitialized] = useAtom(isGoogleMapInitializedAtom);

    const mapSwiperState = useMapSwiperState();

    const updateMapClientUrl = useUpdateMapClientUrl();
    const initialMapType = getStoredMapType();
    const [mapType, setMapType] = React.useState<MapType>(initialMapType);

    const googleMapInstance = useGoogleMapInstance();
    const { resetFilter } = useFilterDataV1Mutation();

    const [updateOnNextIdleEvent, setUpdateOnNextIdleEvent] = React.useState(false);

    const [mapDataDefault, setMapDataDefault] = useAtom(mapDataV5DefaultAtom);
    const mapDataByBoundaries = useAtomValue(mapDataV5ByBoundariesAtom);
    const { defaultLocation, defaultZoom, initialBoundary } =
        useDynamicPageComponentByType<MapDataV5_jsonld_page_read>('MapDataV5')?.meta ?? {};
    const [initialMapPosition] = React.useState({
        latitude: defaultLocation?.latitude,
        longitude: defaultLocation?.longitude,
        zoom: defaultZoom,
    });
    const [mapSize, setMapSize] = useMapSize();

    const mapElement = googleMapInstance?.getDiv();

    React.useEffect(() => {
        const observer = new ResizeObserver((entries) => {
            entries.forEach((entry) => {
                if (mapSize === null) {
                    setMapSize({ height: entry.contentRect.height, width: entry.contentRect.width });
                }
            });
        });

        if (!mapElement) {
            return;
        }

        observer.observe(mapElement);

        return () => {
            observer.disconnect();
        };
    }, [googleMapInstance, mapElement, mapSize, setMapSize]);

    const multiPins = useMultiPins();

    const { filterGroups, filterInfoText, icons: iconsDefault } = mapDataDefault?.attributes ?? {};
    const {
        accommodationFilters,
        activityToggle,
        filterButton,
        icons: iconByBoundaries,
        loadMoreButton,
        pins,
        travelFormCta,
        verticalToggleIconUrl,
        verticalToggleOptions,
    } = mapDataByBoundaries?.attributes ?? {};

    const icons = iconByBoundaries ?? iconsDefault ?? null;

    // remove map state to prevent old data from being used when map is opened again
    React.useEffect(() => {
        return () => {
            setMapDataDefault(null);
            setSelectedMapTileHiddenAtom(false);
            setActivePoiKey(null);
            setMapState('initializing');
        };
    }, [setActivePoiKey, setMapDataDefault, setMapState, setSelectedMapTileHiddenAtom]);

    // Check if map projection is defined and sets the state
    React.useEffect(() => {
        if (!googleMapInstance) {
            setIsMapProjectionInitialized(false);
            return;
        }

        setIsMapProjectionInitialized(!!googleMapInstance.getProjection());

        const projectionChangeListener = google.maps.event.addListener(googleMapInstance, 'projection_changed', () => {
            setIsMapProjectionInitialized(!!googleMapInstance.getProjection());
        });

        return () => {
            setIsMapProjectionInitialized(false);
            google.maps.event.removeListener(projectionChangeListener);
        };
    }, [googleMapInstance, setIsMapProjectionInitialized]);

    // initialize map data
    React.useEffect(() => {
        if (mapState !== 'initializing') {
            return;
        }

        if (!isGoogleMapInitialized) {
            return;
        }

        const googleMapProjection = googleMapInstance?.getProjection();

        if (!googleMapInstance || !googleMapProjection || !isMapProjectionInitialized) {
            return;
        }

        // If an initialBoundary was given, that means we are already at the correct location and don't have to wait
        // for the default request to finish and animate to it
        if (initialBoundary && defaultZoom) {
            setMapBoundaries({
                fromLatitude: initialBoundary.fromLatitude.toString(),
                fromLongitude: initialBoundary.fromLongitude.toString(),
                toLatitude: initialBoundary.toLatitude.toString(),
                toLongitude: initialBoundary.toLongitude.toString(),
                zoom: defaultZoom.toString(),
            });

            if (isMapBottomSheetInitiallyOpen) {
                if (!isTempPositionInitialized) {
                    return;
                }

                // if an activePoiKey is set,
                if (activePoiKey) {
                    setMapState('initialization_finished');
                    return;
                }
                appEvents.emit('move_map_to_temp_position');
                return;
            }

            if (defaultZoom && defaultLocation) {
                googleMapInstance.setZoom(defaultZoom);
                googleMapInstance.setCenter({ lat: defaultLocation.latitude, lng: defaultLocation.longitude });

                google.maps.event.addListenerOnce(googleMapInstance, 'idle', () => {
                    setMapState('initialization_finished');
                });
            } else {
                setMapState('initialization_finished');
            }

            return;
        }

        if (!mapDataDefault || !mapSize) {
            return;
        }

        if (isMapBottomSheetInitiallyOpen) {
            // Because we will not visit the default location, we have to
            // calculate the original boundary manually
            const defaultMeta = mapDataDefault.meta;
            const originalBoundary = convertPositionToBoundary(googleMapProjection, mapSize, {
                location: { lat: defaultMeta.defaultLocation.latitude, lng: defaultMeta.defaultLocation.longitude },
                zoom: defaultMeta.defaultZoom,
            });

            if (!originalBoundary) {
                return;
            }

            setMapBoundaries(getBoundariesObject(originalBoundary, defaultMeta.defaultZoom));

            if (!mapDataByBoundaries || !isTempPositionInitialized) {
                return;
            }

            google.maps.event.addListenerOnce(googleMapInstance, 'idle', () => {
                setMapState('initialization_finished');
            });

            // move directly to temp position
            appEvents.emit('move_map_to_temp_position');

            return;
        }

        google.maps.event.addListenerOnce(googleMapInstance, 'idle', () => {
            setMapState('initialization_finished');
        });
        jumpTo(
            {
                latitude: mapDataDefault.meta.defaultLocation.latitude,
                longitude: mapDataDefault.meta.defaultLocation.longitude,
            },
            mapDataDefault.meta.defaultZoom,
        );
    }, [
        activePoiKey,
        appEvents,
        clientUrl,
        defaultLocation,
        defaultZoom,
        googleMapInstance,
        initialBoundary,
        isGoogleMapInitialized,
        isMapBottomSheetInitiallyOpen,
        isMapProjectionInitialized,
        isTempPositionInitialized,
        jumpTo,
        mapDataByBoundaries,
        mapDataDefault,
        mapSize,
        mapState,
        setMapBoundaries,
        setMapState,
    ]);

    // After every path change, update new history state with current state
    useDynamicPageUrlChange(() => {
        saveMapStateToHistoryState({ poiKey: activePoiKey });
    });

    const onLoadMoreClick = React.useCallback(() => {
        setBoundariesLoadMoreLivePricesTarget(loadMoreButton?.livePricesTargetUrl ?? null);
    }, [loadMoreButton, setBoundariesLoadMoreLivePricesTarget]);

    const [isLoadMoreButtonEnabled, setLoadMoreButtonEnabled] = React.useState(false);
    const isMapDataByBoundariesLoading = useAtomValue(isMapDataV5ByBoundariesLoadingAtom);
    const isAutoLoadMoreFeatureFlagEnabled = useAutoLoadMoreSetting();

    React.useEffect(() => {
        if (!isAutoLoadMoreFeatureFlagEnabled || isMapDataByBoundariesLoading) {
            return;
        }
        setLoadMoreButtonEnabled(true);
    }, [isAutoLoadMoreFeatureFlagEnabled, isMapDataByBoundariesLoading]);

    const onIdle = React.useCallback(() => {
        if (['animating', 'animating_bottom_sheet', 'initializing', 'tempPosition'].includes(mapState)) {
            return;
        }

        setMapState('interactive');

        logMap('Idle Event');

        if (!updateOnNextIdleEvent) {
            return;
        }

        if (!googleMapInstance || !mapDataDefault) {
            return;
        }

        const boundaries = googleMapInstance.getBounds();
        const zoom = googleMapInstance.getZoom();

        if (!boundaries || !zoom) {
            return;
        }
        const boundariesObject = getBoundariesObject(boundaries, zoom);

        setMapBoundaries(boundariesObject);

        if (isMapWebView) {
            triggerBoundariesChange(boundariesObject);
        }

        setUpdateOnNextIdleEvent(false);

        updateMapClientUrl({
            namespace: 'onIdle',
            remove: ['poiId', 'activityAreaId', 'destination'],
            update: ['fromLatitude', 'toLatitude', 'fromLongitude', 'toLongitude', 'zoom'],
        });
    }, [
        googleMapInstance,
        mapDataDefault,
        mapState,
        setMapBoundaries,
        isMapWebView,
        setMapState,
        updateMapClientUrl,
        updateOnNextIdleEvent,
    ]);

    const onMapClick = React.useCallback(() => {
        if (activePoiKey) {
            setActivePoiKey(null);
            setSelectedMapTileHiddenAtom(true);
            saveMapStateToHistoryState({ debugNamespace: 'onMapClick', poiKey: null });

            if (isMapWebView) {
                triggerAppBottomSheet('tip');
            }

            updateMapClientUrl({
                namespace: 'onMapClick',
                remove: ['poiId', 'activityAreaId'],
                update: ['fromLongitude', 'toLongitude', 'fromLatitude', 'toLatitude', 'zoom'],
            });
        }
    }, [
        activePoiKey,
        isMapWebView,
        saveMapStateToHistoryState,
        setActivePoiKey,
        setSelectedMapTileHiddenAtom,
        updateMapClientUrl,
    ]);

    const onZoomControlChange = (eventType: CustomMapZoomControlEventType) => {
        if (!googleMapInstance) {
            return;
        }

        updateMapClientUrl({ namespace: 'onZoomControlChange', remove: ['poiId', 'activityAreaId'] });

        const currentZoom = googleMapInstance.getZoom();

        if (!currentZoom) {
            return;
        }

        if (eventType === 'zoom-in') {
            googleMapInstance.setZoom(currentZoom + 1);
        } else {
            googleMapInstance.setZoom(currentZoom - 1);
        }
    };

    const onClusterClick = (cluster: IMapCluster) => {
        if (!googleMapInstance) {
            return;
        }

        appEvents.emit('detach_temp_position_until_next_animation_is_finished');
        scrollPageContainerToTop();

        google.maps.event.addListenerOnce(googleMapInstance, 'idle', () => {
            updateMapClientUrl({
                namespace: 'onMapClick',
                remove: ['poiId', 'activityAreaId'],
                update: ['fromLongitude', 'toLongitude', 'fromLatitude', 'toLatitude', 'zoom'],
            });
        });

        jumpTo(
            {
                latitude: cluster.point.latitude,
                longitude: cluster.point.longitude,
            },
            cluster.targetZoom ?? (googleMapInstance.getZoom() ?? 5) + 2,
        );
    };

    const updateActivePoi = (poiKey: string) => {
        const iMapMarker = pins?.[poiKey] ?? multiPins[poiKey];
        const isMultiPolygon = iMapMarker?.markerType === 'multiPolygon';

        setActivePoiKey(poiKey);
        setVisitedPois([...visitedPois, poiKey]);
        saveMapStateToHistoryState({
            debugNamespace: 'updateActivePoi',
            poiKey: isDesktopView && !isMultiPolygon ? null : poiKey,
        });
    };

    const onPoiClick = (event: React.MouseEvent, poiKey: string) => {
        event.stopPropagation();
        triggerAppBottomSheet('tip');
        if (poiKey === activePoiKey) {
            setActivePoiKey(null);
            saveMapStateToHistoryState({ debugNamespace: 'onPoiClick', poiKey: null });
        } else {
            updateActivePoi(poiKey);
        }

        if (!clientUrl.includes('poiId')) {
            return;
        }

        if (clientUrl.includes('destination')) {
            updateMapClientUrl({
                namespace: 'onPoiClick',
                remove: ['poiId'],
            });
            return;
        }

        updateMapClientUrl({
            namespace: 'onPoiClick',
            remove: ['poiId'],
            update: ['fromLongitude', 'toLongitude', 'fromLatitude', 'toLatitude', 'zoom'],
        });
    };

    const onMapSwiperTileChange = (tile: MapSwiperTile) => {
        if (!pins) {
            return;
        }
        const iMapMarker = pins[tile.markerKey] ?? multiPins[tile.markerKey];

        if (iMapMarker?.markerType !== 'poi' && iMapMarker?.markerType !== 'multiPolygon') {
            return;
        }

        updateActivePoi(tile.markerKey);
    };

    const updateMapType = (mapType: MapType) => {
        googleMapInstance?.setMapTypeId(mapType);

        setStoredMapType(mapType);
        setMapType(mapType);
    };

    const handleResetFilter = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        resetFilter();
    };

    const idleTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const clearIdleTimeoutRef = React.useCallback(() => {
        if (idleTimeoutRef.current === null) {
            return;
        }

        clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = null;
    }, []);

    useMultiPolygons(onPoiClick);
    useMapPlaceRoutePreview();

    React.useEffect(() => {
        if (!googleMapInstance) {
            return;
        }

        const listeners: google.maps.MapsEventListener[] = [];

        listeners.push(
            googleMapInstance.addListener('click', () => {
                clearIdleTimeoutRef();
                onMapClick();
            }),
        );

        listeners.push(
            googleMapInstance.addListener('idle', () => {
                clearIdleTimeoutRef();

                setGoogleMapInitialized(true);
                onIdle();
            }),
        );

        listeners.push(
            googleMapInstance.addListener('dragstart', () => {
                clearIdleTimeoutRef();
            }),
        );

        listeners.push(
            googleMapInstance.addListener('dragend', () => {
                clearIdleTimeoutRef();
                if (mapState !== 'tempPosition') {
                    setUpdateOnNextIdleEvent(true);
                }
            }),
        );

        listeners.push(
            googleMapInstance.addListener('zoom_changed', () => {
                clearIdleTimeoutRef();
                if (mapState !== 'tempPosition') {
                    setUpdateOnNextIdleEvent(true);
                }
            }),
        );

        return () => {
            listeners.forEach((listener) => {
                google.maps.event.removeListener(listener);
            });
        };
    }, [
        appEvents,
        clearIdleTimeoutRef,
        googleMapInstance,
        isGoogleMapInitialized,
        mapState,
        onIdle,
        onMapClick,
        setGoogleMapInitialized,
        updateMapClientUrl,
    ]);

    return {
        accommodationFilters,
        activityToggle,
        filterButton,
        filterGroups,
        filterInfoText,
        handleResetFilter,
        icons,
        initialMapPosition,
        isLoadMoreButtonEnabled,
        loadMoreButton,
        mapSwiperState,
        mapType,
        onClusterClick,
        onLoadMoreClick,
        onMapSwiperTileChange,
        onPoiClick,
        onZoomControlChange,
        pins,
        travelFormCta,
        updateMapType,
        verticalToggleIconUrl,
        verticalToggleOptions,
    };
}
